import { type TZDate, TZDateMini } from "@date-fns/tz";
import { addDays, endOfDay, isSameDay, startOfDay, startOfMonth, subDays } from "date-fns";
import { Day, type ICalendarCell, type TCalendarEvent, type TCalendarEventRange } from "./types";

export const isWeekendDay = (date: TZDate) => {
  return (date.getDay() + 1) % 6 !== 1;
};

export const getEventSortedWithDateRange = <T>(
  events: TCalendarEvent<T>[],
  timeZone: string
): TCalendarEventRange<T>[] => {
  return events
    .map(
      (event) =>
        (({
          ...event,
          startRange: startOfDay(new TZDateMini(event.startDate, timeZone)),
          endRange: endOfDay(new TZDateMini(event.endDate ?? event.startDate, timeZone))
        }) as const),
      []
    )
    .sort((a, b) => {
      const startRangeDelta = a.startRange.getTime() - b.startRange.getTime();
      if (startRangeDelta === 0 || (isSameDay(a.startRange, b.startRange) && !isSameDay(a.endRange, b.endRange))) {
        return b.endRange.getTime() - a.endRange.getTime();
      }
      return startRangeDelta;
    });
};

export const getEventsPositionDelta = <T>(
  firstEvent: TCalendarEvent<T> | undefined,
  line: number,
  positionCache: Record<number, Record<string, number>>
) => {
  if (!firstEvent || positionCache[line]?.[firstEvent.id] === undefined) {
    return 0;
  }
  return positionCache[line][firstEvent.id];
};

export const getEventsForCurrentCell = <T>(
  events: TCalendarEventRange<T>[],
  cellDate: TZDate,
  line: number,
  positionCache: Record<number, Record<string, number>>
): TCalendarEventRange<T>[] => {
  return events.reduce<TCalendarEventRange<T>[]>((acc, event, index) => {
    if (cellDate.getTime() >= event.startRange.getTime() && cellDate.getTime() <= event.endRange.getTime()) {
      if (positionCache[line] === undefined) {
        positionCache[line] = { [event.id]: acc.length };
      } else if (positionCache[line][event.id] === undefined) {
        positionCache[line][event.id] = acc.length;
      }
      acc.push(event);
    }
    return acc;
  }, []);
};

export const getCalendarCells = <T>(
  events: TCalendarEvent<T>[],
  date: TZDate,
  cellPerLine: number,
  timeZone: string,
  startOfWeekDay = Day.MONDAY,
  skipWeekends = true
) => {
  const positionCache: Record<number, Record<string, number>> = {};
  const eventsWithRange = getEventSortedWithDateRange(events, timeZone);
  const cells: ICalendarCell<T>[] = [];
  let currentDate = startOfMonth(date);
  currentDate = subDays(currentDate, currentDate.getDay() - startOfWeekDay);
  while (
    currentDate.getTime() < date.getTime() ||
    currentDate.getMonth() === date.getMonth() ||
    currentDate.getDay() !== startOfWeekDay
  ) {
    if (!skipWeekends || isWeekendDay(currentDate)) {
      const currentLine = Math.floor(cells.length / cellPerLine);
      const currentCellEvents = getEventsForCurrentCell(eventsWithRange, currentDate, currentLine, positionCache);
      cells.push({
        date: currentDate,
        disabled: currentDate.getMonth() !== date.getMonth(),
        eventsPositionOffset: getEventsPositionDelta(currentCellEvents[0], currentLine, positionCache),
        events: currentCellEvents,
      });
    }
    currentDate = addDays(currentDate, 1);
  }
  return cells;
};
